import type { AccountLevel, IUser } from '@/shared/api/types/user-types'
import { useTranslation } from 'react-i18next'
import { ACCOUNT_LEVEL_VALUES, useUpdateUserAccountLevelMutation } from '@/entities/user'
import { useAdminScope } from '@/shared/hooks/use-admin-scope'
import { getApiErrorText } from '@/shared/libs/get-api-error-text'
import { showToast } from '@/shared/libs/toast'
import { FormField } from '@/shared/ui/form-field'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'

interface UserAccountLevelSelectProps {
  user: IUser
}

const ACCOUNT_LEVEL_LABEL_KEYS: Record<AccountLevel, string> = {
  SUPER_ADMIN: 'user.accountLevelOptions.superAdmin',
  TENANT_ADMIN: 'user.accountLevelOptions.tenantAdmin',
  USER: 'user.accountLevelOptions.user',
}

export function UserAccountLevelSelect({ user }: UserAccountLevelSelectProps) {
  const { t } = useTranslation()
  const { isSuperAdmin } = useAdminScope()
  const mutation = useUpdateUserAccountLevelMutation()
  const disabled = !isSuperAdmin || mutation.isPending

  async function handleChange(value: string) {
    if (!ACCOUNT_LEVEL_VALUES.includes(value as AccountLevel)) return
    try {
      await mutation.mutateAsync({
        id: user.id,
        payload: { accountLevel: value as AccountLevel },
      })
      showToast.success(t('user.accountLevelUpdateSuccess'))
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    }
  }

  return (
    <FormField
      label={t('user.accountLevel')}
      hint={isSuperAdmin ? undefined : t('user.accountLevelHintRestricted')}
    >
      <Select value={user.accountLevel ?? ''} onValueChange={handleChange} disabled={disabled}>
        <SelectTrigger>
          <SelectValue placeholder={t('user.accountLevel')} />
        </SelectTrigger>
        <SelectContent>
          {ACCOUNT_LEVEL_VALUES.map((level) => (
            <SelectItem key={level} value={level}>
              {t(ACCOUNT_LEVEL_LABEL_KEYS[level])}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FormField>
  )
}
